import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Users, Plus, Loader2, Trash2, Ban, CheckCircle2, Copy, Megaphone, MessageCircle, DollarSign } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { staff } from "@/lib/api";
import { formatDate } from "@/lib/format";

const roleMeta = {
  marketing: { label: "Marketing", icon: Megaphone, color: "bg-blue-50 text-blue-700 border-blue-200", desc: "Buat campaign, kelola template, lihat performa" },
  cs: { label: "Customer Service", icon: MessageCircle, color: "bg-emerald-50 text-emerald-700 border-emerald-200", desc: "Lihat customer, update status, lihat reward" },
  finance: { label: "Finance", icon: DollarSign, color: "bg-amber-50 text-amber-700 border-amber-200", desc: "Lihat profit, SKU analytics, export laporan" },
};

const empty = { email: "", name: "", role: "marketing", password: "" };

export default function TeamAccess() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [newlyCreated, setNewlyCreated] = useState(null);

  const fetchList = async () => {
    setLoading(true);
    try { setRows(await staff.list()); } catch (err) { console.warn("Staff load failed:", err); }
    setLoading(false);
  };
  useEffect(() => { fetchList(); }, []);

  const create = async () => {
    if (!form.email || !form.name || !form.password) { toast.error("Semua field wajib diisi"); return; }
    if (form.password.length < 6) { toast.error("Password minimal 6 karakter"); return; }
    try {
      const created = await staff.create(form);
      setNewlyCreated({ ...created, password: form.password });
      setOpen(false);
      setForm(empty);
      fetchList();
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal"); }
  };

  const toggle = async (s) => {
    await staff.update(s.id, { active: !s.active });
    toast.success(s.active ? "Staff dinonaktifkan" : "Staff diaktifkan");
    fetchList();
  };
  const remove = async (s) => {
    if (!window.confirm(`Hapus akses ${s.name}?`)) return;
    await staff.remove(s.id); toast.success("Staff dihapus"); fetchList();
  };
  const copy = (t) => { navigator.clipboard.writeText(t); toast.success("Disalin"); };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold text-gray-900">Team Access</h1>
          <p className="text-sm text-gray-500 mt-1">Undang staff Anda dengan akses terbatas sesuai role.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="add-staff-btn"><Plus className="mr-2 h-4 w-4" /> Undang Staff</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Undang Staff Baru</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Nama Lengkap</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="staff-name" /></div>
              <div><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="staff-email" /></div>
              <div>
                <Label>Role</Label>
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger data-testid="staff-role"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="marketing">Marketing — Buat campaign & template</SelectItem>
                    <SelectItem value="cs">Customer Service — Lihat customer & reward</SelectItem>
                    <SelectItem value="finance">Finance — Lihat profit & analytics</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Password Sementara (minimal 6 karakter)</Label>
                <Input type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="Contoh: Custora2026" data-testid="staff-password" />
                <p className="text-xs text-gray-500 mt-1">Berikan password ini ke staff Anda via WhatsApp. Mereka bisa ganti nanti.</p>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Batal</Button>
              <Button onClick={create} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="save-staff-btn">Kirim Undangan</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid md:grid-cols-3 gap-4">
        {Object.entries(roleMeta).map(([k, v]) => {
          const Icon = v.icon;
          return (
            <Card key={k} className="border-gray-200"><CardContent className="p-5">
              <div className={`w-10 h-10 rounded-lg ${v.color} border flex items-center justify-center`}><Icon size={18} /></div>
              <div className="mt-3 font-heading font-semibold text-gray-900">{v.label}</div>
              <p className="mt-1 text-xs text-gray-500">{v.desc}</p>
            </CardContent></Card>
          );
        })}
      </div>

      <Card className="border-gray-200 overflow-hidden">
        {loading ? <div className="p-16 flex justify-center"><Loader2 className="animate-spin text-emerald-600" /></div>
          : rows.length === 0 ? (
            <div className="py-16 text-center">
              <Users className="mx-auto text-gray-300" size={56} />
              <h3 className="mt-4 font-heading text-lg font-semibold text-gray-900">Belum ada staff</h3>
              <p className="mt-1 text-sm text-gray-500">Undang tim marketing, CS, atau finance untuk bantu operasional toko.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider border-b border-gray-200">
                  <tr>
                    <th className="px-4 py-3">Nama</th>
                    <th className="px-4 py-3">Email</th>
                    <th className="px-4 py-3">Role</th>
                    <th className="px-4 py-3">Status</th>
                    <th className="px-4 py-3">Diundang</th>
                    <th className="px-4 py-3"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {rows.map((s) => (
                    <tr key={s.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 font-semibold text-gray-900">{s.name}</td>
                      <td className="px-4 py-3 text-gray-700">{s.email}</td>
                      <td className="px-4 py-3"><Badge className={`${roleMeta[s.role]?.color} border`}>{roleMeta[s.role]?.label}</Badge></td>
                      <td className="px-4 py-3">
                        {s.active ? <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 border">Aktif</Badge>
                          : <Badge className="bg-gray-100 text-gray-600 border-gray-200 border">Non-aktif</Badge>}
                      </td>
                      <td className="px-4 py-3 text-xs text-gray-500">{formatDate(s.created_at)}</td>
                      <td className="px-4 py-3">
                        <div className="flex gap-1">
                          <Button size="sm" variant="outline" onClick={() => toggle(s)} data-testid={`toggle-staff-${s.id}`}>
                            {s.active ? <Ban size={14} /> : <CheckCircle2 size={14} className="text-emerald-600" />}
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => remove(s)}><Trash2 size={14} className="text-red-500" /></Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
      </Card>

      <Dialog open={!!newlyCreated} onOpenChange={(v) => !v && setNewlyCreated(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Staff berhasil dibuat!</DialogTitle></DialogHeader>
          <p className="text-sm text-gray-600">Berikan kredensial berikut kepada staff Anda:</p>
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 font-mono text-sm space-y-1">
            <div><b>Email:</b> {newlyCreated?.email}</div>
            <div><b>Password:</b> {newlyCreated?.password}</div>
            <div><b>Role:</b> {roleMeta[newlyCreated?.role]?.label}</div>
            <div><b>Login URL:</b> {window.location.origin}/staff-login</div>
          </div>
          <Button onClick={() => copy(`Halo ${newlyCreated?.name}, Anda diundang bergabung ke tim ${newlyCreated?.role} di Custora. Email: ${newlyCreated?.email}, Password: ${newlyCreated?.password}. Login di: ${window.location.origin}/staff-login`)} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="copy-staff-invite">
            <Copy className="mr-2 h-4 w-4" /> Copy pesan WhatsApp
          </Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
